import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import axios from "axios";
import { apiURL } from "../config/config";
import ApprovalActionModal from "./ApprovalActionModal";
import ApprovalTableDesktop from "./ApprovalTableDesktop";
import ApprovalTableMobile from "./ApprovalTableMobile";
import { toast } from "react-toastify";

const ApprovalTable = ({ quotations }) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const getUserData = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    return { email: user?.username || "", level: user?.level || 0 };
  };
  const { level } = getUserData();

  const openModal = (actionType, qt) => {
    setSelectedAction(actionType);
    setSelectedQuotation(qt);
    setReason(qt.reason || "");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setReason("");
    setError("");
  };

  const updateApprovalStatus = async () => {
    if (!selectedQuotation) return;
    const { email, level } = getUserData();
    const token = localStorage.getItem("token");
    const approvalId = selectedQuotation.approvalHierarchy[0]?._id;
    if (!approvalId) return;

    if (selectedAction === "cancel" && !reason.trim()) {
      setError("กรุณากรอกเหตุผลในการยกเลิก");
      return;
    }

    try {
      setLoading(true);

      await axios.patch(
        `${apiURL}approvals/${approvalId}/approvers`,
        {
          level,
          approver: email,
          status: selectedAction === "reject" ? "Rejected" : "Canceled",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (selectedAction === "reject" || selectedAction === "cancel") {
        await axios.patch(
          `${apiURL}quotations/${selectedQuotation._id}/reason`,
          { reason },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      toast.success("Status updated successfully ✅");
      closeModal();
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error(error);
      toast.error("เกิดข้อผิดพลาด ❌");
    } finally {
      setLoading(false);
    }
  };

  const approveQuotation = async (qt) => {
    const { email, level } = getUserData();
    const token = localStorage.getItem("token");
    const approvalId = qt.approvalHierarchy[0]?._id;
    if (!approvalId) return;

    try {
      setLoading(true);

      await axios.patch(
        `${apiURL}approvals/${approvalId}/approvers`,
        {
          level,
          approver: email,
          status: "Approved",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Quotation approved successfully ✅");
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error(error);
      toast.error("เกิดข้อผิดพลาด ❌");
    } finally {
      setLoading(false);
    }
  };

  const confirmApprove = async (qt) => {
    const result = await Swal.fire({
      title: `ยืนยันการอนุมัติ QT-(${qt.type})-${qt.runNumber}`,
      text: "คุณต้องการอนุมัติใบเสนอราคานี้ใช่หรือไม่",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2563eb", // indigo-600
      cancelButtonColor: "#d33",
      confirmButtonText: "อนุมัติเลย",
      cancelButtonText: "ยกเลิก",
    });

    if (result.isConfirmed) {
      approveQuotation(qt);
    }
  };

  const confirmReject = async (qt) => {
    const result = await Swal.fire({
      title: `ยืนยันการ Reject ใบเสนอราคา QT-(${qt.type})-${qt.runNumber}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626", // red-600
      cancelButtonColor: "#6b7280", // gray-500
      confirmButtonText: "ปฏิเสธ",
      cancelButtonText: "ยกเลิก",
    });

    if (result.isConfirmed) {
      openModal("reject", qt);
    }
  };

  const confirmCancel = async (qt) => {
    const result = await Swal.fire({
      title: `ยืนยันการยกเลิกใบเสนอราคา QT-(${qt.type})-${qt.runNumber}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6b7280", // gray-500
      cancelButtonColor: "#d33",
      confirmButtonText: "ยกเลิกใบเสนอราคา",
      cancelButtonText: "กลับ",
    });

    if (result.isConfirmed) {
      openModal("cancel", qt);
    }
  };

  return (
    <div className="relative">
      {loading && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50">
          <ClipLoader color="#ffffff" size={80} />
        </div>
      )}

      {/* ✅ Table Container */}
      <div
        className="rounded-lg shadow-lg border border-gray-100 dark:border-gray-700 
             bg-white dark:bg-gray-900 overflow-hidden transition-colors duration-300"
      >
        <div className="hidden lg:block">
          <ApprovalTableDesktop
            quotations={quotations}
            confirmApprove={confirmApprove}
            confirmReject={confirmReject}
            confirmCancel={confirmCancel}
            navigate={navigate}
            level={level}
          />
        </div>

        <div className="block lg:hidden">
          <ApprovalTableMobile
            quotations={quotations}
            confirmApprove={confirmApprove}
            confirmReject={confirmReject}
            confirmCancel={confirmCancel}
            navigate={navigate}
            level={level}
          />
        </div>
      </div>

      <ApprovalActionModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={updateApprovalStatus}
        actionType={selectedAction}
        reason={reason}
        setReason={setReason}
        error={error}
        loading={loading}
      />
    </div>
  );
};

export default ApprovalTable;
